/**
 * Service desk settings — Step 95.
 *
 * Mined from Kai's `Service Desk Settings.html`: the two blocks the reference
 * puts under this name, minus what we have no feature for yet (public ticket
 * links, sharing — neither exists here, so there is nothing to switch).
 *
 * Pure, so the two rules that actually protect something are tested rather
 * than trusted:
 *
 *  - **Upload types can be NARROWED, never widened.** Step 73 built
 *    `uploadSafety` with a per-purpose allowlist in CODE, because a file type
 *    is an attack surface and the validator is what closes it. An admin
 *    setting that could ADD a type would re-open that surface from a settings
 *    page. The reference's list is global and widenable; ours diverges on
 *    purpose: `narrowAccept` returns the intersection, so the setting can only
 *    ever remove.
 *  - **Sign-up domains gate SELF-service, not agents.** "Who can sign up or
 *    create tickets" applies to the portal and to inbound email — the
 *    channels where nobody on the desk chose the person. An agent adding a
 *    supplier from an outside domain is a decision, and the rule does not
 *    second-guess it.
 */

export type DateFormat = 'dmy' | 'mdy' | 'ymd';
export type TimeFormat = '24h' | '12h';

export interface ServiceDeskSettings {
  language: string;
  dateFormat: DateFormat;
  timeFormat: TimeFormat;
  timeZone: string;
  currency: string;
  /** Include binned/spam/merged tickets in report counts. Off by default —
   *  the reference warns it changes every metric. */
  showTrashedInReports: boolean;
  security: {
    /** Content types the `attachment` purpose may accept. `null` = the code
     *  default; a list can only narrow it. */
    uploadTypes: string[] | null;
    /** Email domains that may self-serve. Empty = anyone. */
    signupDomains: string[];
    /** Force sign-out after this many minutes since sign-in. `null` = the
     *  cookie's own lifetime. */
    sessionTimeoutMinutes: number | null;
  };
  /**
   * Step 171 — how requesters get into the portal.
   *
   * Kai: *"the portal should default to ms365, but have the ability via a
   * separate url to not — all our customers are internal, and should all have
   * a ms account."* So Microsoft is the front door and the emailed link lives
   * at its own address, for the people who are the exception rather than the
   * rule: a contractor, a personal address, somebody whose account is locked
   * out of the very thing they are ringing about.
   */
  /**
   * Step 177 — how every reply on this desk opens.
   *
   * Kai: *"it's an admin setting no?"* Yes, and the reason is the word
   * "every": how a desk greets its customers is a house style, not forty
   * personal preferences. A signature is the opposite — it carries somebody's
   * own name and job title, so that stays on their profile.
   *
   * Empty means the shared default in `replyTemplate.ts`, the same rule the
   * rest of this file follows: never store the default, so changing it later
   * still reaches everybody who did not choose otherwise.
   */
  replyTemplate: string;
  portal: {
    /** Offer Microsoft on the portal sign-in page. Ignored when Microsoft
     *  sign-in is not configured at all — the page falls back to the emailed
     *  link rather than offering a door that goes nowhere. */
    microsoftSignIn: boolean;
    /**
     * Create a requester the first time an unknown Microsoft account signs in
     * to the portal.
     *
     * ON by default, and the opposite of the agent rule, which never
     * auto-provisions. The difference is what a successful sign-in proves:
     * against the organisation's OWN tenant it proves the person is in the
     * organisation, which is exactly what being a requester means here.
     * Turn it off for a deployment whose requesters are not all internal.
     */
    autoProvision: boolean;
    /** Show the emailed-link form on the main portal sign-in page as well as
     *  at its own address. Off: one obvious door, and a quiet way past it. */
    emailLinkOnMainPage: boolean;
  };
}

/** UK defaults — we are a UK product, and the reference's own default for
 *  this tenant was Day-Month-Year, 24-hour, London. */
export const DEFAULT_SERVICE_DESK: ServiceDeskSettings = {
  language: 'en-GB',
  dateFormat: 'dmy',
  timeFormat: '24h',
  timeZone: 'Europe/London',
  currency: 'GBP',
  showTrashedInReports: false,
  security: { uploadTypes: null, signupDomains: [], sessionTimeoutMinutes: null },
  replyTemplate: '',
  portal: { microsoftSignIn: true, autoProvision: true, emailLinkOnMainPage: false },
};

/**
 * A stored value is a claim, not a fact — it came from a JSON column that a
 * hand or an older release may have written. Everything is checked and
 * anything unrecognised falls back to the default for THAT field, so a bad
 * time zone cannot take the date format down with it.
 */
export function parseServiceDesk(raw: unknown): ServiceDeskSettings {
  const d = DEFAULT_SERVICE_DESK;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { ...d, security: { ...d.security } };
  const r = raw as Record<string, unknown>;
  const sec = (r.security && typeof r.security === 'object' ? r.security : {}) as Record<string, unknown>;
  const portal = (r.portal && typeof r.portal === 'object' ? r.portal : {}) as Record<string, unknown>;
  const str = (v: unknown, fb: string) => (typeof v === 'string' && v.trim() ? v.trim() : fb);
  const oneOf = <T extends string>(v: unknown, allowed: readonly T[], fb: T): T =>
    typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : fb;
  const domains = Array.isArray(sec.signupDomains)
    ? sec.signupDomains
        .filter((x): x is string => typeof x === 'string')
        .map(normaliseDomain)
        .filter(Boolean)
    : [];
  const types = Array.isArray(sec.uploadTypes)
    ? sec.uploadTypes.filter((x): x is string => typeof x === 'string' && x.includes('/'))
    : null;
  const timeout =
    typeof sec.sessionTimeoutMinutes === 'number' && sec.sessionTimeoutMinutes >= 5
      ? Math.floor(sec.sessionTimeoutMinutes)
      : null;
  return {
    language: str(r.language, d.language),
    dateFormat: oneOf(r.dateFormat, ['dmy', 'mdy', 'ymd'] as const, d.dateFormat),
    timeFormat: oneOf(r.timeFormat, ['24h', '12h'] as const, d.timeFormat),
    timeZone: validTimeZone(str(r.timeZone, d.timeZone)) ? str(r.timeZone, d.timeZone) : d.timeZone,
    currency: str(r.currency, d.currency).toUpperCase().slice(0, 3),
    showTrashedInReports: r.showTrashedInReports === true,
    security: { uploadTypes: types, signupDomains: [...new Set(domains)], sessionTimeoutMinutes: timeout },
    /**
     * Each flag read INDEPENDENTLY and defaulted on its own, like every other
     * field here: a stored value is a claim, not a fact. `!== false` rather
     * than `=== true` for the two that default on, so a settings object
     * written before this block existed keeps the documented default instead
     * of silently turning the portal's front door off.
     */
    replyTemplate: typeof r.replyTemplate === 'string' ? r.replyTemplate : '',
    portal: {
      microsoftSignIn: portal.microsoftSignIn !== false,
      autoProvision: portal.autoProvision !== false,
      emailLinkOnMainPage: portal.emailLinkOnMainPage === true,
    },
  };
}

export function validTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** `@Example.COM.` and `example.com` are the same domain. */
export function normaliseDomain(raw: string): string {
  return raw.trim().toLowerCase().replace(/^@/, '').replace(/\.$/, '');
}

/**
 * The accept list a purpose actually uses: the code's list, narrowed by the
 * admin's. NEVER widened — anything in the setting the code does not permit is
 * ignored, which is what keeps a settings page from re-opening a surface the
 * validator closed.
 */
export function narrowAccept(codeAccept: readonly string[], setting: string[] | null): string[] {
  if (!setting) return [...codeAccept];
  const wanted = new Set(setting);
  const narrowed = codeAccept.filter((t) => wanted.has(t));
  // An admin who removed everything has locked uploads entirely. That is a
  // legitimate choice; an EMPTY intersection is exactly what they asked for.
  return narrowed;
}

/**
 * May this address self-serve (sign up, raise a ticket by portal or email)?
 *
 * Empty list = anyone. Matches the domain and any subdomain of it, because
 * `mail.example.com` addresses are still Example's people and an allowlist
 * that made them retype their domain is one nobody maintains.
 */
export function emailDomainAllowed(email: string, signupDomains: readonly string[]): boolean {
  if (!signupDomains.length) return true;
  const at = email.lastIndexOf('@');
  if (at < 0) return false;
  const domain = normaliseDomain(email.slice(at + 1));
  return signupDomains.some((d) => domain === d || domain.endsWith('.' + d));
}

/** Preview strings for the settings screen — the same formatting the app
 *  will use, so what the admin sees is what agents get. */
/**
 * The sample instant is a fixed UTC moment — 13:30Z, which is 14:30 in the UK
 * default zone — NOT a local-time constructor. `new Date(2026, 8, 5, 14, 30)`
 * meant "14:30 wherever this code runs", so the preview (and the test) shifted
 * with the machine's zone: CI runs in UTC and saw 15:30. A fixed instant reads
 * the same everywhere; only `settings.timeZone` moves it, which is the point.
 */
export const SAMPLE_INSTANT = new Date(Date.UTC(2026, 8, 5, 13, 30));

export function formatSample(settings: ServiceDeskSettings, at: Date = SAMPLE_INSTANT): string {
  const order: Record<DateFormat, Intl.DateTimeFormatOptions & { locale: string }> = {
    dmy: { locale: 'en-GB', day: '2-digit', month: 'short', year: 'numeric' },
    mdy: { locale: 'en-US', month: 'short', day: '2-digit', year: 'numeric' },
    ymd: { locale: 'sv-SE', year: 'numeric', month: '2-digit', day: '2-digit' },
  };
  const { locale, ...opts } = order[settings.dateFormat];
  const date = new Intl.DateTimeFormat(locale, { ...opts, timeZone: settings.timeZone }).format(at);
  const time = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: settings.timeFormat === '12h',
    timeZone: settings.timeZone,
  }).format(at);
  return `${date}, ${time}`;
}
